import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ReservationAdminService } from '../../../_services/api/reservation-admin.service';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';
import { MenuStateService } from '../../../_services/app/menu-state.service';
import { AgentAdminService } from '../../../_services/api/agent-admin.service';

import { 
  ReservationDetail, 
  OfferWithActions,
  ReservationClientStatus,
  ReservationAdminStatus,
} from '../../../_models/reservation.model';
import { OfferUtilsService } from '../../../_utils/offer-utils.service';
import { AdminReservationOfferCardComponent } from '../admin-reservation-offer-card/admin-reservation-offer-card.component';


@Component({
  selector: 'app-admin-reservation-details',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminReservationOfferCardComponent],
  templateUrl: './admin-reservation-details.component.html',
  styleUrl: './admin-reservation-details.component.scss',
})
export class AdminReservationDetailsComponent implements OnInit {
  reservationId!: number;
  reservation: ReservationDetail | null = null;
  offersWithActions: OfferWithActions[] = [];
  isLoading = false;
  errorMessage: string | null = null;
  
  menuOpen = false;
  showDeleteModal = false;
  showDeleteOfferModal = false;
  selectedOfferId: number | null = null;
  
  private subscriptions: Subscription[] = [];

  showConfirmModal = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reservationService: ReservationAdminService,
    private agentService: AgentAdminService,
    private offerUtils: OfferUtilsService,
    private notification: NotificationService,
    private elementRef: ElementRef, 
    private menuStateService: MenuStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {    
    this.loadReservationId();
    if(this.reservationId){
      this.menuStateService.activeMenu$.subscribe(activeId => {
      this.menuOpen = activeId === this.reservationId;
    });
      if (isPlatformBrowser(this.platformId)) {
          document.addEventListener('click', this.closeMenu.bind(this));
      }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
        document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

 private loadReservationId(): void {
    const routeSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      
      if (id) {
        this.reservationId = +id;
        this.loadReservationDetails();
      } else {
        this.errorMessage = 'ID de réservation non trouvé';
        this.goToList();
      }
    });
    this.subscriptions.push(routeSub);
  }

  private loadReservationDetails(): void {
    this.isLoading = true;
    this.errorMessage = null;

    const detailsSub = this.reservationService.getReservationDetails(this.reservationId).subscribe({
      next: (response: ApiResponse<ReservationDetail>) => {
        if (response.success && response.data) {
          this.reservation = response.data;
          this.prepareOffers();
        } else {
          this.errorMessage = response.message || 'Erreur lors du chargement des données';
        }
        setTimeout(() => {this.isLoading = false; 
          if (isPlatformBrowser(this.platformId)) {
          window.scrollTo({top: 0, behavior: 'smooth'});
          }}, 300);        
      },
      error: (error) => {
        console.error('Error loading reservation details:', error);
        this.errorMessage = 'Erreur de chargement des détails du reservation';
        this.isLoading = false;
        
        if (error.status === 404) {
          this.errorMessage = 'Réservation non trouvée';
          setTimeout(() => this.goToList(), 2000);
        }
      }
    });
    this.subscriptions.push(detailsSub);
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.reservationId);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  goToList(): void {
    this.router.navigate(['/admin/reservations']);
  }

  formatAddress(address: any): string {
    if (!address) return 'Non spécifiée';
    
    const parts: string[] = [];
    
    if (address.streetNumber && address.street) {
      parts.push(`${address.street} ${address.streetNumber}`);
    } else if (address.street) {
      parts.push(address.street);
    }
    
    if (address.postalCode && address.city) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else if (address.postalCode) {
      parts.push(address.postalCode);
    } else if (address.city) {
      parts.push(address.city);
    }
    
    if (parts.length === 0) {
      return 'Non spécifiée';
    }
    
    return parts.join(', ') + ".";
  }
  
  confirmDelete(): void {
    this.menuOpen = false;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
  }

 deleteReservation(): void {
    if(!this.reservation){return}
    /*this.reservationService.deleteReservation(this.reservation.id).subscribe({
      next: () => {
        this.showDeleteModal = false;
        this.goToList();
      },
      error: (error) => {
        console.error('Error deleting reservation:', error);
        this.notification.showError('Erreur lors de la suppression cette reservation');
        this.showDeleteModal = false;
      }
    });*/
  }

  // offer managment
  private prepareOffers(): void {
    if (!this.reservation || !this.reservation.offers) {
      this.offersWithActions = [];
      return;
    }

    this.offersWithActions = this.offerUtils.getOffersWithActions(this.reservation.offers);
    this.offersWithActions = this.offerUtils.sortOffersByStatus(this.offersWithActions);
  }

  canCreateNewOffer(): boolean {
    if (!this.reservation) return false;
    return this.offerUtils.canCreateOfferForReservation(this.reservation.reservationAdminStatus);
  }

  createNewOffer(): void {
    if (!this.reservationId) return;
    this.router.navigate(['/admin/reservations', this.reservationId, 'offers', 'create']);
  }

  onOfferdeleted(event : any): void {
    this.loadReservationDetails();
  }  

  onAgentdeleted(event : any): void {
    this.loadReservationDetails();
  }  

  //reservation status
  getReservationAdminStatusClass(): string {
    return this.offerUtils.getReservationOverallStatusClass(this.reservation!.reservationClientStatus, this.reservation!.reservationAdminStatus);
  }

  getReservationAdminStatusText(): string {
    return this.offerUtils.getReservationOverallStatusText(this.reservation!.reservationClientStatus, this.reservation!.reservationAdminStatus);
  }

  canConfirmReservation(){
    return this.reservation?.reservationClientStatus === ReservationClientStatus.ACCEPTED 
      && this.reservation?.reservationAdminStatus === ReservationAdminStatus.WAITING_CONFIRMATION; 
  }


  onConfirmReservation(): void {
    if (!this.reservation) {
      this.notification.showError('Aucune reservation trouvée');
      return;
    }

    this.menuOpen = false;
    this.showConfirmModal = true;
  }

  confirmReservation(): void {
    if (!this.reservation) return;

    this.showConfirmModal = false;

    this.reservationService.confirmReservation(this.reservationId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(/*response.message || */'Reservation confirmée avec succès');
          this.loadReservationDetails();
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors du confirmations de la reservation');
        }
      });
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }
}